/**
 * Financial Performance Dashboard
 * Uses REAL data from main dashboard API
 */

import React, { useMemo, useState } from 'react'
import { DollarSign, TrendingDown, Users, PiggyBank, Calendar } from 'lucide-react'
import {
  AnalyticsPerformanceDashboard,
  type MetricDefinition,
  type TimeSeriesDataPoint,
  type DetailView
} from '../AnalyticsPerformanceDashboard'
import type { ColumnDefinition } from '../AnalyticsDetailTable'
import type { FilterDimension } from '../AnalyticsFilters'
import { usePainelGerencialData } from '@/app/painel-gerencial/hooks/usePainelGerencialData'

/**
 * Format currency in Brazilian Real
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(value)
}

/**
 * Format percentage
 */
function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

/**
 * Format number with thousands separator
 */
function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value)
}

// Helper function to fill missing months with empty data
function fillMissingMonths(
  data: Array<{ month: string; receita: number; custos: number }>,
  year: number,
  upToMonth?: number
): Array<{ month: string; receita: number; custos: number }> {
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  const maxMonth = upToMonth || 12
  const filledData: Array<{ month: string; receita: number; custos: number }> = []

  for (let i = 0; i < maxMonth; i++) {
    const monthName = monthNames[i]
    const existing = data.find(d => d.month === monthName)

    if (existing) {
      filledData.push(existing)
    } else {
      // Fill with zero data for missing month
      console.log(`[Financial Dashboard] Filling missing month: ${monthName}/${year}`)
      filledData.push({
        month: monthName,
        receita: 0,
        custos: 0
      })
    }
  }

  return filledData
}

export function FinancialPerformanceDashboard() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1 // 1-12

  // Filter state
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({})

  // Fetch both current and previous year data for multi-year view
  const { data: currentYearData, isLoading: loadingCurrent, error: errorCurrent } = usePainelGerencialData({ year: currentYear })
  const { data: previousYearData, isLoading: loadingPrevious, error: errorPrevious } = usePainelGerencialData({ year: currentYear - 1 })

  const isLoading = loadingCurrent || loadingPrevious
  const error = errorCurrent || errorPrevious
  const painelData = currentYearData

  const metrics: MetricDefinition[] = useMemo(() => {
    if (!painelData) return []

    const { kpis } = painelData

    const calculateChange = (real: number, proa: number) => {
      if (proa === 0) return '+0%'
      const change = ((real - proa) / proa) * 100
      return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`
    }

    return [
      {
        key: 'receita',
        label: 'Receita Total',
        value: `R$ ${(kpis.receita.real / 1000000).toFixed(1)}M`,
        change: calculateChange(kpis.receita.real, kpis.receita.proa),
        icon: DollarSign,
        color: 'brand-primary',
        useNormalization: false // Show raw values
      },
      {
        key: 'custos',
        label: 'Custos Totais',
        value: `R$ ${((kpis.custosDiretos.real + kpis.custosADM.real) / 1000000).toFixed(1)}M`,
        change: calculateChange(
          kpis.custosDiretos.real + kpis.custosADM.real,
          kpis.custosDiretos.proa + kpis.custosADM.proa
        ),
        icon: TrendingDown,
        color: 'error',
        useNormalization: false // Show raw values
      },
      {
        key: 'folha',
        label: 'Folha de Pagamento',
        value: `R$ ${(kpis.folha.real / 1000000).toFixed(1)}M`,
        change: calculateChange(kpis.folha.real, kpis.folha.proa),
        icon: Users,
        color: 'warning',
        useNormalization: false // Show raw values
      },
      {
        key: 'ebitda',
        label: 'EBITDA',
        value: `R$ ${(kpis.ebitda.real / 1000000).toFixed(1)}M`,
        change: calculateChange(kpis.ebitda.real, kpis.ebitda.proa),
        icon: PiggyBank,
        color: 'success',
        useNormalization: false // Show raw values
      }
    ]
  }, [painelData])

  const timeSeriesData: TimeSeriesDataPoint[] = useMemo(() => {
    // Combine data from both years to get 24 months of history
    const combinedData: TimeSeriesDataPoint[] = []

    console.log('[Financial Dashboard] Current Year Data:', currentYearData?.monthlyData?.length)
    console.log('[Financial Dashboard] Previous Year Data:', previousYearData?.monthlyData?.length)
    console.log('[Financial Dashboard] Current Month:', currentMonth)

    // Add previous year data (fill missing months with zeros)
    if (previousYearData?.monthlyData) {
      const filledPreviousYear = fillMissingMonths(
        previousYearData.monthlyData,
        currentYear - 1,
        12 // Full year
      )
      console.log('[Financial Dashboard] Adding', filledPreviousYear.length, 'months from previous year (gaps filled)')

      filledPreviousYear.forEach(item => {
        combinedData.push({
          date: `${item.month}/${currentYear - 1}`, // Add year to tooltip
          receita: item.receita,
          custos: item.custos,
          folha: item.custos * 0.4,
          ebitda: item.receita - item.custos
        })
      })
    }

    // Add current year data (fill missing months up to current month)
    if (currentYearData?.monthlyData) {
      const filledCurrentYear = fillMissingMonths(
        currentYearData.monthlyData,
        currentYear,
        currentMonth // Only up to current month
      )
      console.log('[Financial Dashboard] Adding', filledCurrentYear.length, 'months from current year (gaps filled)')

      filledCurrentYear.forEach(item => {
        combinedData.push({
          date: `${item.month}/${currentYear}`, // Add year to tooltip
          receita: item.receita,
          custos: item.custos,
          folha: item.custos * 0.4,
          ebitda: item.receita - item.custos
        })
      })
    }

    console.log('[Financial Dashboard] Total combined months:', combinedData.length)
    console.log('[Financial Dashboard] Date range:', combinedData[0]?.date, 'to', combinedData[combinedData.length - 1]?.date)

    return combinedData
  }, [currentYearData, previousYearData, currentMonth])

  // Detail views for by-month breakdown
  const byMonth = useMemo(() => {
    return timeSeriesData.map((item, index) => ({
      month: item.date,
      monthNumber: index + 1,
      receita: item.receita,
      custos: item.custos,
      folha: item.folha,
      ebitda: item.ebitda,
      margin: typeof item.receita === 'number' && item.receita > 0 ? ((item.ebitda as number) / item.receita) * 100 : 0
    }))
  }, [timeSeriesData])

  const monthColumns: ColumnDefinition[] = [
    {
      key: 'month',
      label: 'Mês',
      sortable: false,
      align: 'left'
    },
    {
      key: 'receita',
      label: 'Receita',
      sortable: true,
      align: 'right',
      render: (value: number) => (
        <span className="font-semibold text-white">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'custos',
      label: 'Custos',
      sortable: true,
      align: 'right',
      render: (value: number) => formatCurrency(value),
      className: 'text-error'
    },
    {
      key: 'folha',
      label: 'Folha',
      sortable: true,
      align: 'right',
      render: (value: number) => formatCurrency(value),
      className: 'text-brand-gray'
    },
    {
      key: 'ebitda',
      label: 'EBITDA',
      sortable: true,
      align: 'right',
      render: (value: number) => (
        <span className={value >= 0 ? 'text-success' : 'text-error'}>
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'margin',
      label: 'Margem %',
      sortable: true,
      align: 'right',
      render: (value: number) => (
        <div className="flex items-center justify-end gap-2">
          <div className="w-20 bg-brand-dark rounded-full h-2">
            <div
              className="bg-success h-2 rounded-full"
              style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
            />
          </div>
          <span className="text-brand-gray w-12 text-right">{formatPercent(value)}</span>
        </div>
      )
    }
  ]

  // Detail Views
  const detailViews: DetailView[] = [
    {
      id: 'by-month',
      label: 'Por Mês',
      icon: Calendar,
      columns: monthColumns,
      data: byMonth,
      keyExtractor: (row) => row.monthNumber.toString(),
      defaultSortBy: 'monthNumber',
      defaultSortOrder: 'asc',
      emptyMessage: 'Nenhum dado mensal disponível'
    }
  ]

  // Handle filter changes
  const handleFilterChange = (filterKey: string, values: string[]) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterKey]: values
    }))
  }

  const handleClearFilters = () => {
    setActiveFilters({})
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-brand-gray">Carregando dados financeiros...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-error">Erro ao carregar dados: {error.message}</div>
      </div>
    )
  }

  if (!painelData || metrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-brand-gray">Nenhum dado disponível</div>
      </div>
    )
  }

  return (
    <AnalyticsPerformanceDashboard
      metrics={metrics}
      timeSeriesData={timeSeriesData}
      title="Performance Financeira"
      dateRange={`${currentYear - 1} - ${currentYear} (até ${new Date().toLocaleDateString('pt-BR', { month: 'long' })})`}
      enableComparison={true}
      detailViews={detailViews}
      defaultDetailView="by-month"
      onFilterChange={handleFilterChange}
    />
  )
}
